import * as Sentry from '@sentry/browser';
import dropin from 'braintree-web-drop-in';
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import makeApiRequest from '../../utils/makeApiRequest';
import makePrivateApiRequest from '../../utils/makePrivateApiRequest';

import { API_ENDPOINTS, PRIVATE_API_ENDPOINTS } from '../../constants';
import Button from './Button';
import Header from './Header';
import SelectSimple from './SelectSimple';

const STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

const ERROR_TYPES = {
  BRAINTREE_INIT_ERROR: "We couldn't load the payment form. Please refresh the page and try again.",
  PAYMENT_METHOD_ERROR: "We couldn't process your card. Please check your details and try again.",
  PORTIN_ERROR: "We couldn't port in your number. Please try again.",
  NEW_NUMBER_ERROR: "We couldn't assign a new number. Please try again.",
  GENERIC_ERROR: "We couldn't set up your card. Please try again.",
};

const provisionNewNumber = async (data, customerId, setError) => {
  const formatedData = {
    zipCode: data.zip,
    CustomerId: customerId,
    orderType: 'New',
  };

  try {
    const response = await makeApiRequest(API_ENDPOINTS.ACTIVATION_NEW, formatedData);
    return response.nextStep;
  } catch (error) {
    setError(ERROR_TYPES.NEW_NUMBER_ERROR);
    throw error;
  }
};

const deleteOrignalPortin = async (customerId) => {
  try {
    const deletResponse = await makeApiRequest(
      API_ENDPOINTS.ACTIVATION_TRANSFER_DELETE,
      {
        CustomerId: customerId,
      },
      'DELETE',
    );

    console.log(deletResponse);
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
  }
};

const provisionPortin = async (data, customerId, setError, onPortinFail) => {
  try {
    // Check if we need to delete the previous request
    if (data.resubmit) {
      await deleteOrignalPortin(customerId);
    }

    const response = await makeApiRequest(
      API_ENDPOINTS.ACTIVATION_TRANSFER,
      {
        ...data,
        CustomerId: customerId,
      },
      'POST',
      true,
    );

    if (response.Status === false) {
      onPortinFail(response.error);
      throw new Error(`Failed to provision portin | Error | ${JSON.stringify(response)}`);
    }

    if (!response?.data?.nextStep) {
      throw new Error(`Failed to provision portin | No next step provided | ${JSON.stringify(response)}`);
    }

    return response.data.nextStep;
  } catch (error) {
    setError(ERROR_TYPES.PORTIN_ERROR);
    throw error;
  }
};

const StepCard = ({
  address,
  provisionData,
  customerId,
  onAccountActivated,
  onPortinFailed,
  status,
  setStatus,
  onPortinSubmited,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isResubmit, setIsResubmit] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isExistingAddress, setIsExistingAddress] = useState(address ? true : false);
  const [isInitializing, setIsInitializing] = useState(!status.cardVaulted);
  const [requires3ds, setRequires3ds] = useState(false);

  const dropinInstanceRef = useRef(null);
  const dropinContainerRef = useRef(null);

  // Initialize Braintree Drop-in on mount (skip if card already vaulted on a prior submit)
  useEffect(() => {
    if (status.cardVaulted) {
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        const response = await makePrivateApiRequest(PRIVATE_API_ENDPOINTS.DEFAULT_SUBSCRIPTION_TYPE, {
          customer_id: customerId,
        });

        if (response?.gateway?.code !== 'braintree' || response?.key !== 'token') {
          throw new Error(`Unexpected gateway response | ${JSON.stringify(response)}`);
        }

        const clientToken = response.token.value;
        const needs3ds = response['3ds'] === true;

        if (cancelled) return;
        setRequires3ds(needs3ds);

        const instance = await dropin.create({
          authorization: clientToken,
          container: dropinContainerRef.current,
          threeDSecure: needs3ds,
          card: {
            vault: { vaultCard: true },
          },
        });

        if (cancelled) {
          instance.teardown().catch(console.error);
          return;
        }

        dropinInstanceRef.current = instance;
        setIsInitializing(false);
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        if (!cancelled) {
          setSubmitError(ERROR_TYPES.BRAINTREE_INIT_ERROR);
          setIsInitializing(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
      if (dropinInstanceRef.current) {
        dropinInstanceRef.current.teardown().catch(console.error);
        dropinInstanceRef.current = null;
      }
    };
  }, []);

  // Resubmit port-in (card was already vaulted server-side on the prior submit)
  useEffect(async () => {
    if (status.cardVaulted && provisionData && !provisionData.isNew && !status.portinSubmitted && !status.provisioned) {
      setIsResubmit(true);

      try {
        const nextStep = await provisionPortin(provisionData, customerId, setSubmitError, onPortinFailed);
        setStatus((current) => ({ ...current, portinSubmitted: true }));
        onPortinSubmited(nextStep);
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        setIsResubmit(false);
        setSubmitError((current) => current || ERROR_TYPES.GENERIC_ERROR);
      }
    }
  }, []);

  const handleToggleAddress = (e) => {
    setIsExistingAddress(e.target.checked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitError(null);

    try {
      if (!dropinInstanceRef.current) {
        throw new Error('Drop-in instance not initialized');
      }

      // Request payment method from Drop-in (handles 3DS + vault internally via SDK options)
      await dropinInstanceRef.current.requestPaymentMethod(
        requires3ds ? { threeDSecure: { amount: '0.01' } } : undefined,
      );
      setStatus((current) => ({ ...current, cardVaulted: true }));

      if (provisionData) {
        // New number submission
        if (provisionData.isNew) {
          if (!status.provisioned) {
            await provisionNewNumber(provisionData, customerId, setSubmitError);
            setStatus((current) => ({ ...current, provisioned: true }));
          }

          onAccountActivated();
        }

        // Portin submission
        if (!provisionData.isNew) {
          if (!status.portinSubmitted && !status.provisioned) {
            const nextStep = await provisionPortin(provisionData, customerId, setSubmitError, onPortinFailed);
            setStatus((current) => ({ ...current, portinSubmitted: true }));
            onPortinSubmited(nextStep);
          } else {
            onAccountActivated();
          }
        }
      } else {
        onAccountActivated();
      }
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      setIsLoading(false);

      // Drop-in surfaces validation errors via its own UI; only show our message for non-validation failures
      const isValidationError = error?.name === 'BraintreeError' && error?.code?.startsWith?.('DROPIN_');
      if (!isValidationError) {
        setSubmitError((current) => current || ERROR_TYPES.GENERIC_ERROR);
      } else {
        setSubmitError((current) => current || ERROR_TYPES.PAYMENT_METHOD_ERROR);
      }
    }
  };

  return html`
    <${Header}
      title="Set Up Your Payment"
      description="No charges until renewal. Cancel any time."
      eyebrow="Alomst done!"
    />
    <form
      class="flex flex-col gap-3 items-stretch max-w-[490px] w-full group relative ${isResubmit
        ? 'max-h-80 overflow-hidden opacity-35 loading'
        : ''}"
      onSubmit=${handleSubmit}
    >
      <div class="flex gap-2.5 md:items-center">
        <div class="w-4 md:w-6 opacity-20">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="54" viewBox="0 0 40 54" fill="none">
            <path
              d="M38.8889 20H35.5555V15.5555C35.5555 6.97812 28.5775 0 20 0C11.4225 0 4.44448 6.97812 4.44448 15.5555V20H1.11115C0.496979 20 0 20.497 0 21.1111V48.889C0 51.3401 1.99323 53.3333 4.44448 53.3333H35.5556C38.0068 53.3333 40 51.3401 40 48.8889V21.1111C40 20.497 39.503 20 38.8889 20ZM23.3269 43.2107C23.3616 43.5243 23.2607 43.839 23.0502 44.0745C22.8397 44.3099 22.538 44.4445 22.2223 44.4445H17.7778C17.4621 44.4445 17.1604 44.3099 16.9499 44.0745C16.7394 43.8391 16.6384 43.5244 16.6732 43.2107L17.3742 36.9076C16.2359 36.0797 15.5556 34.77 15.5556 33.3333C15.5556 30.8822 17.5489 28.8889 20.0001 28.8889C22.4514 28.8889 24.4446 30.8821 24.4446 33.3333C24.4446 34.77 23.7643 36.0797 22.626 36.9076L23.3269 43.2107ZM28.8889 20H11.1111V15.5555C11.1111 10.6543 15.0987 6.66667 20 6.66667C24.9012 6.66667 28.8889 10.6543 28.8889 15.5555V20Z"
              fill="black"
            />
          </svg>
        </div>
        <div class="flex flex-col mr-auto">
          <h3 class="flex h5 normal-case leading-very-tight">Credit/Debit Card</h3>
          <p class="text-xs text-gray-500">Secure and encrypted</p>
        </div>
        <div class="">
          <div class="flex gap-0.5">
            <img
              alt="VISA"
              src="https://cdn.shopify.com/shopifycloud/checkout-web/assets/c1.en/assets/visa.sxIq5Dot.svg"
              width="38"
              height="24"
            />
            <img
              alt="MASTERCARD"
              src="https://cdn.shopify.com/shopifycloud/checkout-web/assets/c1.en/assets/mastercard.1c4_lyMp.svg"
              width="38"
              height="24"
            />
            <img
              alt="AMEX"
              src="https://cdn.shopify.com/shopifycloud/checkout-web/assets/c1.en/assets/amex.Csr7hRoy.svg"
              width="38"
              height="24"
            />
            <img
              class="hidden md:block"
              alt="DISCOVER"
              src="https://cdn.shopify.com/shopifycloud/checkout-web/assets/c1.en/assets/discover.C7UbFpNb.svg"
              width="38"
              height="24"
            />
          </div>
        </div>
      </div>

      <div
        class="flex flex-col gap-3 bg-white p-5 rounded-xl shadow-lg border border-dashed border-gray-400 min-h-[120px]"
      >
        ${(isInitializing || isResubmit) &&
        html`
          <div class="animate-pulse flex flex-col gap-3" aria-hidden="true">
            <div class="h-10 bg-slate-200 rounded-lg"></div>
            <div class="grid grid-cols-2 gap-3">
              <div class="h-10 bg-slate-200 rounded-lg"></div>
              <div class="h-10 bg-slate-200 rounded-lg"></div>
            </div>
            <div class="h-10 bg-slate-200 rounded-lg"></div>
          </div>
        `}
        <div ref=${dropinContainerRef} id="braintree-dropin"></div>
      </div>

      <h3 class="flex h5 mt-4 -mb-0.5 normal-case">Billing Address</h3>
      <div class="flex flex-col gap-2 bg-white p-5 rounded-xl shadow-sm">
        ${address &&
        html`
          <label class="flex gap-3 relative group cursor-pointer">
            <input
              type="checkbox"
              class="absolute opacity-0 peer"
              id="address"
              name="isExistingAddress"
              checked=${isExistingAddress}
              onChange=${handleToggleAddress}
            />
            <span
              class="flex items-center w-6 h-6 p-1 text-white rounded-lg border border-gray-300 group-has-[:checked]:border-purple shrink-0 relative group-has-[:checked]:before:scale-100 before:scale-0 before:absolute before:w-full before:h-full before:rounded-[6px] before:bg-purple before:top-1/2 before:left-1/2 before:transform before:-translate-x-1/2 before:-translate-y-1/2 before:transition-transform"
            >
              <span class="opacity-0 group-has-[:checked]:opacity-100 transition-opacity relative">
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="11" viewBox="0 0 13 11" fill="currentColor">
                  <path
                    d="M12.6979 1.12478C12.2957 0.722019 11.6426 0.722273 11.2398 1.12478L4.67723 7.68761L1.76045 4.77085C1.35769 4.36809 0.704826 4.36809 0.302068 4.77085C-0.100689 5.17361 -0.100689 5.82647 0.302068 6.22923L3.94789 9.87505C4.14914 10.0763 4.41303 10.1772 4.67695 10.1772C4.94087 10.1772 5.20502 10.0766 5.40627 9.87505L12.6979 2.58313C13.1007 2.18065 13.1007 1.52751 12.6979 1.12478Z"
                  />
                </svg>
              </span>
            </span>

            <span class="flex flex-col gap-1">
              <span class="text-base leading-none font-serif">Same As Account Address</span>
              <span class="text-gray-500 text-xs"
                >${`${address?.address}, ${address?.city}, ${address?.state}, ${address?.zip}`}</span
              >
            </span>
          </label>
        `}
        ${!isExistingAddress &&
        html`
          ${address && html` <div class="w-full h-px border-t border-gray-200 my-3"></div> `}
          <div class="flex flex-col gap-2.5">
            <fieldset class="flex flex-col">
              <label class="text-xs mb-1">Address<span class="text-red-600">*</span></label>
              <input
                type="text"
                class="grow min-w-0 px-5 py-3 md:py-5 w-full rounded-xl text-base lg:text-sm focus:outline-none border-gray-300 border"
                name="address"
                placeholder="Address"
                aria-label="Address"
                required
              />
            </fieldset>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
              <fieldset class="flex flex-col">
                <label class="text-xs mb-1">City<span class="text-red-600">*</span></label>
                <input
                  type="text"
                  class="grow min-w-0 px-5 py-3 md:py-5 w-full rounded-xl text-base lg:text-sm focus:outline-none border-gray-300 border"
                  name="city"
                  placeholder="City"
                  aria-label="City"
                  required
                />
              </fieldset>
              <fieldset class="flex flex-col">
                <label class="text-xs mb-1">State<span class="text-red-600">*</span></label>
                <${SelectSimple} name="state" placeholder="Select State" options=${STATES} required />
              </fieldset>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
              <fieldset class="flex flex-col">
                <label class="text-xs mb-1">ZIP Code<span class="text-red-600">*</span></label>
                <input
                  type="text"
                  class="grow min-w-0 px-5 py-3 md:py-5 w-full rounded-xl text-base lg:text-sm focus:outline-none border-gray-300 border"
                  name="zip"
                  placeholder="ZIP Code"
                  aria-label="ZIP Code"
                  required
                />
              </fieldset>
            </div>
          </div>
        `}
      </div>
      <${Button} text="Complete Your Activation" loading=${isLoading} disabled=${isInitializing} className="mt-3" />
      ${submitError && html`<p class="text-red-500 text-center text-sm">${submitError}</p>`}
    </form>
  `;
};

export default StepCard;
